/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from '@/components/markdown/MermaidDiagram';
import {
  PrismSyntaxHighlighter as SyntaxHighlighter,
  prismDarkStyle,
  prismLightStyle,
  resolveSyntaxLanguage,
} from '@/lib/prismHighlighter';
import { useTheme } from '@/context/ThemeContext';
import { isMermaidLanguage, normalizeMermaidMarkdown } from '@/utils/mermaidMarkdown';

/**
 * MarkdownRenderer - Renders markdown content with syntax highlighting
 * 
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists, etc.)
 * - Syntax highlighting for code blocks
 * - Theme-aware (dark/light mode support)
 * - Copy button for code blocks
 */
const MarkdownRenderer = memo(({ content, className = '' }) => {
  const { isDark } = useTheme();
  const normalizedContent = useMemo(() => normalizeMermaidMarkdown(content), [content]);
  
  // Custom code block component with syntax highlighting
  // eslint-disable-next-line no-unused-vars
  const CodeBlock = ({ node, inline, className: codeClassName, children, ...props }) => {
    const match = /language-([\w-]+)/.exec(codeClassName || '');
    const language = match ? match[1] : '';
    const resolvedLanguage = resolveSyntaxLanguage(language);
    const codeString = String(children).replace(/\n$/, '');
    
    // Handle inline code
    if (inline) {
      return (
        <code
          style={{
            backgroundColor: isDark ? '#374151' : '#f3f4f6',
            color: isDark ? '#f9fafb' : '#1f2937',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.875em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    if (isMermaidLanguage(language)) {
      return <MermaidDiagram chart={codeString} isDark={isDark} />;
    }
    
    // Handle code blocks with syntax highlighting
    return (
      <div style={{ position: 'relative', margin: '8px 0' }}>
        {language && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '11px',
              color: isDark ? '#9ca3af' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {language}
          </div>
        )}
        <SyntaxHighlighter
          style={isDark ? prismDarkStyle : prismLightStyle}
          language={resolvedLanguage}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '8px',
            fontSize: '13px',
            padding: '16px',
          }}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  };
  
  // Custom components for markdown elements
  const components = {
    code: CodeBlock,
    // Style tables
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '8px 0' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '14px',
          }}
        >
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
          backgroundColor: isDark ? '#374151' : '#f3f4f6',
          textAlign: 'left',
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
        }}
      >
        {children}
      </td>
    ),
    // Style blockquotes
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderLeft: `4px solid ${isDark ? '#6b7280' : '#d1d5db'}`,
          margin: '8px 0',
          paddingLeft: '16px',
          color: isDark ? '#9ca3af' : '#6b7280',
          fontStyle: 'italic',
        }}
      >
        {children}
      </blockquote>
    ),
    // Style links
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: isDark ? '#60a5fa' : '#2563eb',
          textDecoration: 'underline',
        }}
      >
        {children}
      </a>
    ),
    // Style lists
    ul: ({ children }) => (
      <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '8px 0', paddingLeft: '24px' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ margin: '4px 0' }}>{children}</li>
    ),
    // Style headings
    h1: ({ children }) => (
      <h1 style={{ fontSize: '1.5em', fontWeight: 700, margin: '16px 0 8px' }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.3em', fontWeight: 600, margin: '14px 0 6px' }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1.1em', fontWeight: 600, margin: '12px 0 4px' }}>{children}</h3>
    ),
    // Style paragraphs
    p: ({ children }) => (
      <p style={{ margin: '8px 0', lineHeight: 1.6 }}>{children}</p>
    ),
    // Style horizontal rules
    hr: () => (
      <hr
        style={{
          border: 'none',
          borderTop: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          margin: '16px 0',
        }}
      />
    ),
  };
  
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
